<template>
  <div class="security-guard-messages">
    <h2 class="subheading grey--text">Mis Comunicados</h2>
    <v-container class="my-5">
      <v-alert v-if="securityError" type="error" dismissible class="mb-3">
        {{ securityError }}
      </v-alert>

      <v-layout v-if="isLoading" align-center justify-center row>
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-layout>

      <v-layout v-else-if="myMessages.length === 0" align-center justify-center row class="mt-5">
        <v-flex xs12 text-xs-center>
          <v-icon size="80" color="grey lighten-2" class="mb-3">notifications_none</v-icon>
          <h3 class="grey--text">No tienes comunicados</h3>
          <p class="grey--text mt-2">Los comunicados de tu empresa aparecerán aquí</p>
        </v-flex>
      </v-layout>

      <v-layout v-else row wrap>
        <v-flex xs12 v-for="message in sortedMessages" :key="message.id" class="mb-4">
          <v-card :class="getCardClass(message.is_read)" class="elevation-2 cursor-pointer" @click="openMessage(message)">
            <v-list-tile avatar class="pa-3">
              <v-list-tile-avatar :color="getPriorityColor(message.priority)">
                <v-icon dark>{{ getPriorityIcon(message.priority) }}</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{ message.title }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ formatDate(message.created_at) }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon v-if="message.is_read" color="green">check_circle</v-icon>
                <v-icon v-else color="orange">mail</v-icon>
              </v-list-tile-action>
            </v-list-tile>

            <v-card-text class="pt-0">
              <p>{{ truncateContent(message.content) }}</p>
              <p class="grey--text caption mb-0">
                <span v-if="message.is_read">✓ Leído el {{ formatDate(message.read_at) }}</span>
                <span v-else class="orange--text font-weight-bold">⏳ Sin leer</span>
              </p>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- MODAL: Ver Comunicado Completo -->
    <v-dialog v-model="showMessageModal" max-width="700px">
      <v-card v-if="selectedMessage">
        <v-toolbar :color="getPriorityColor(selectedMessage.priority)" dark>
          <v-toolbar-title>{{ selectedMessage.title }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-chip small text-color="white">
            <v-icon left small>{{ getPriorityIcon(selectedMessage.priority) }}</v-icon>
            {{ getPriorityDisplay(selectedMessage.priority) }}
          </v-chip>
          <v-btn icon color="white" flat @click="showMessageModal = false" class="ml-2">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <p class="grey--text"><strong>Enviado:</strong> {{ formatDate(selectedMessage.created_at) }}</p>

          <v-divider class="my-3"></v-divider>
          <div class="message-content">{{ selectedMessage.content }}</div>
          <v-divider class="my-3"></v-divider>

          <v-alert v-if="selectedMessage.is_read" type="success" :value="true" icon="check_circle">
            Leído el {{ formatDate(selectedMessage.read_at) }}
          </v-alert>
          <v-alert v-else type="warning" :value="true" icon="info">
            Este comunicado aún no ha sido marcado como leído
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="showMessageModal = false">Cerrar</v-btn>
          <v-btn
            v-if="!selectedMessage.is_read"
            color="primary"
            @click="markAsRead"
            :loading="isLoading"
          >
            <v-icon left>check</v-icon>
            Marcar como Leído
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  FETCH_MY_MESSAGES,
  MARK_MESSAGE_AS_READ
} from '@/store/modules/security'
import {
  getPriorityColor,
  getPriorityIcon,
  getPriorityDisplay,
  formatMessageDate
} from '@/utils/securityMessageHelpers'

export default {
  name: 'security_guard_messages',
  computed: {
    ...mapGetters('security', {
      isLoading: 'isSecurityLoading',
      securityError: 'getSecurityError',
      myMessages: 'getMyMessages'
    }),
    sortedMessages() {
      return [...this.myMessages].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }
  },
  data() {
    return {
      showMessageModal: false,
      selectedMessage: null
    }
  },
  mounted() {
    this.fetchMyMessages()
  },
  methods: {
    ...mapActions('security', [
      FETCH_MY_MESSAGES,
      MARK_MESSAGE_AS_READ
    ]),
    getPriorityColor,
    getPriorityIcon,
    getPriorityDisplay,
    formatDate: formatMessageDate,
    fetchMyMessages() {
      const token = this.$store.getters.getToken
      this[FETCH_MY_MESSAGES](token)
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al cargar comunicados')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    openMessage(message) {
      this.selectedMessage = message
      this.showMessageModal = true
    },
    markAsRead() {
      const token = this.$store.getters.getToken
      this[MARK_MESSAGE_AS_READ]({
        token,
        messageReadId: this.selectedMessage.id
      })
        .then(() => {
          this.$store.commit('snackbar_change_message', 'Comunicado marcado como leído')
          this.$store.commit('snackbar_change_color', 'success')
          this.$store.commit('snackbar_activate_true')
          this.selectedMessage.is_read = true
          this.selectedMessage.read_at = new Date().toISOString()
        })
        .catch(() => {
          this.$store.commit('snackbar_change_message', this.securityError || 'Error al marcar como leído')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_activate_true')
        })
    },
    truncateContent(content) {
      if (!content) return ''
      return content.length > 150 ? content.substring(0, 150) + '...' : content
    },
    getCardClass(isRead) {
      return isRead ? 'blue-grey lighten-5' : 'orange lighten-5'
    }
  }
}
</script>

<style scoped>
.security-guard-messages {
  padding: 20px;
}

.subheading {
  font-size: 24px;
  font-weight: 300;
}

.cursor-pointer {
  cursor: pointer;
  transition: box-shadow 0.3s ease;
}

.cursor-pointer:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
}

.message-content {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
  font-size: 16px;
}
</style>
